import React, { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Trash2 } from "lucide-react";
import { logError } from "@/lib/errorHandling";
import { debug } from "@/lib/debug";
import { captureError } from "@/lib/sentry";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isClearing: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    isClearing: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, isClearing: false };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    debug.error("ErrorBoundary caught an error:", error, errorInfo);
    
    // Create an enhanced error object with component stack info
    const enhancedError = new Error(error.message);
    enhancedError.stack = `${error.stack}\n\nComponent Stack:${errorInfo.componentStack}`;
    
    // Report to Sentry first, then internal logger
    captureError(enhancedError, { componentStack: errorInfo.componentStack });
    logError("unknown", "react_crash", enhancedError);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null, isClearing: false });
  };

  private handleClearCacheAndReload = async () => {
    this.setState({ isClearing: true });
    
    try {
      // Clear sessionStorage
      sessionStorage.clear();
      
      // Clear localStorage
      localStorage.clear();
      
      // Clear IndexedDB databases
      if ('indexedDB' in window) {
        const databases = await indexedDB.databases?.();
        if (databases) {
          await Promise.all(
            databases.map((db) => {
              if (db.name) {
                return new Promise<void>((resolve) => {
                  const request = indexedDB.deleteDatabase(db.name!);
                  request.onsuccess = () => resolve();
                  request.onerror = () => resolve();
                  request.onblocked = () => resolve();
                });
              }
              return Promise.resolve();
            })
          );
        }
      }
      
      // Reload the page
      window.location.reload();
    } catch (e) {
      debug.error("Failed to clear cache:", e);
      // Reload anyway
      window.location.reload();
    }
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="text-center max-w-md">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-8 w-8 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Something went wrong
            </h1>
            <p className="text-muted-foreground mb-6">
              We're sorry, but something unexpected happened. Please try refreshing the page.
            </p>
            {import.meta.env.DEV && this.state.error && (
              <div className="mt-6 mb-6 text-left bg-destructive/5 border border-destructive/20 rounded-lg p-4 max-w-lg">
                <h3 className="text-sm font-semibold text-destructive mb-2">
                  Error Details (Dev Only)
                </h3>
                <pre className="text-xs text-muted-foreground overflow-auto max-h-48 whitespace-pre-wrap">
                  {this.state.error.message}
                  {'\n\n'}
                  {this.state.error.stack}
                </pre>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={this.handleReset} className="gap-2">
                <RefreshCw className="h-4 w-4" />
                Try Again
              </Button>
              <Button 
                variant="outline" 
                onClick={this.handleClearCacheAndReload}
                disabled={this.state.isClearing}
                className="gap-2"
              >
                <Trash2 className="h-4 w-4" />
                {this.state.isClearing ? "Clearing..." : "Clear Cache & Reload"}
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
